import styled from 'styled-components';
import {Space} from 'antd';

const StyledColumnEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledColumnEditorLocked = styled.div`
  color: ${(props) => props.theme.colors.neutral_8};
  font-size: 12px;
`;

const StyledColumnEditorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  // The "Columns" title (the header's own bare text node, as opposed to the Space/badge siblings
  // next to it) -- primary copy, darkened to match the wizard's other primary labels.
  > span:first-child {
    color: ${(props) => props.theme.colors.neutral_10};
  }
`;

const StyledColumnEditorCount = styled.span`
  margin-left: auto;
  color: ${(props) => props.theme.colors.neutral_8};
  font-size: 12px;
`;

const StyledColumnEditorAddTrigger = styled.div`
  .ant-btn-link {
    padding-left: 0;
  }
`;

// Shared by the head row and every data row, so the "Field"/"Example" labels line up with their
// own column underneath (design review: "should look like a table", examples/05).
const columnGridTemplate = '1fr 1fr 28px';

const StyledColumnEditorList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;
`;

const StyledColumnEditorHeadRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: ${columnGridTemplate};
  gap: 8px;
  padding: 4px 0 8px;
  background-color: ${(props) => props.theme.colors.neutral_1};
  color: ${(props) => props.theme.colors.neutral_8};
  font-size: 12px;
`;

const StyledColumnEditorRow = styled.div`
  display: grid;
  grid-template-columns: ${columnGridTemplate};
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.neutral_3};

  &:last-child {
    border-bottom: none;
  }

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // The field name (the row's own bare text node) -- primary copy, as opposed to the example
  // value next to it (StyledColumnEditorExample), which stays secondary.
  > span:first-child {
    color: ${(props) => props.theme.colors.neutral_10};
  }
`;

const StyledColumnEditorExample = styled.span`
  color: ${(props) => props.theme.colors.neutral_8};
  font-size: 12px;
`;

const StyledColumnEditorSavedNotice = styled(Space)`
  color: ${(props) => props.theme.colors.neutral_8};
  font-size: 12px;
  white-space: nowrap;
`;

// The available-fields panel now renders inline, below the "+ Add field" trigger, instead of in a
// floating Dropdown overlay (examples/20261027_214611.png) -- a light grey well the search box and
// rows sit inset into, not a shadowed popup. Its own rounded corners and inset (rather than bleeding
// to the modal's edges, whose exact padding this component has no reliable way to read) keep it
// self-contained regardless of whatever padding wraps it.
const StyledColumnEditorAddPanel = styled.div`
  background-color: ${(props) => props.theme.colors.additional_bg_2};
  border-radius: ${(props) => props.theme.border_radius.standard};
  margin-top: 8px;
  padding: 12px;
`;

const StyledColumnEditorSearchInput = styled.input`
  display: block;
  width: 100%;
  border: 1px solid ${(props) => props.theme.colors.neutral_4};
  border-radius: 999px;
  background-color: ${(props) => props.theme.colors.neutral_1};
  padding: 8px 16px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${(props) => props.theme.colors.primary_6};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.neutral_8};
  }
`;

const StyledColumnEditorAddList = styled.div`
  max-height: 220px;
  overflow-y: auto;
  margin-top: 8px;
`;

// Same Field/Example grid as the selected-columns rows (`StyledColumnEditorRow`), but zebra-striped
// against the panel's own background instead of divided by borders, with a trailing "+" in place of
// the "×" (design review: the add-field list should read as the same table, not a checkbox list).
const StyledColumnEditorAddRow = styled.div`
  display: grid;
  grid-template-columns: ${columnGridTemplate};
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: ${(props) => props.theme.border_radius.standard};

  &:nth-child(even) {
    background-color: ${(props) => props.theme.colors.neutral_1};
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.primary_2};
  }

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Same primary/secondary split as StyledColumnEditorRow above.
  > span:first-child {
    color: ${(props) => props.theme.colors.neutral_10};
  }

  .ant-btn {
    color: ${(props) => props.theme.colors.primary_6};
  }
`;

const StyledColumnEditorAddEmpty = styled.div`
  padding: 6px 8px;
  color: ${(props) => props.theme.colors.neutral_8};
  font-size: 12px;
`;

export {
  StyledColumnEditor,
  StyledColumnEditorLocked,
  StyledColumnEditorHeader,
  StyledColumnEditorCount,
  StyledColumnEditorAddTrigger,
  StyledColumnEditorList,
  StyledColumnEditorHeadRow,
  StyledColumnEditorRow,
  StyledColumnEditorExample,
  StyledColumnEditorSavedNotice,
  StyledColumnEditorAddPanel,
  StyledColumnEditorSearchInput,
  StyledColumnEditorAddList,
  StyledColumnEditorAddRow,
  StyledColumnEditorAddEmpty,
};
